/**
 * Navigation Configuration
 *
 * Centralized navigation menu items for the app layout.
 * Uses i18n keys for labels with fallbacks.
 */

import {
  LayoutDashboard,
  FileText,
  FileBadge,
  FileSpreadsheet,
  ClipboardList,
  Users,
  Briefcase,
  Building2,
  CreditCard,
  Package,
  ShieldCheck,
  UserCog,
  Settings,
  HelpCircle,
  MoreHorizontal,
  Handshake,
} from 'lucide-react';
import { ROUTE_PATHS } from '@/navigation/constants';
import type { NavigationItem, NavigationSection } from '../types';

/**
 * Main navigation items
 * These appear in the sidebar and drawer
 */
export const MAIN_NAV_ITEMS: NavigationItem[] = [
  {
    id: 'overview',
    labelKey: 'navigation:menu.overview',
    label: 'Overview',
    icon: LayoutDashboard,
    path: ROUTE_PATHS.OVERVIEW,
  },
  {
    id: 'cover-notes',
    labelKey: 'navigation:menu.coverNotes',
    label: 'Cover Notes',
    icon: FileBadge,
    path: ROUTE_PATHS.COVER_NOTES,
  },
  {
    id: 'policies',
    labelKey: 'navigation:menu.policies',
    label: 'Policies',
    icon: FileText,
    path: ROUTE_PATHS.POLICIES,
    permission: 'policies:view',
  },
  {
    id: 'claims',
    labelKey: 'navigation:menu.claims',
    label: 'Claims',
    icon: ClipboardList,
    path: ROUTE_PATHS.CLAIMS,
    permission: 'claims:view',
  },
  {
    id: 'customers',
    labelKey: 'navigation:menu.customers',
    label: 'Customers',
    icon: Users,
    path: ROUTE_PATHS.CUSTOMERS,
    // Visible to all authenticated users - customer-specific permissions can be added later
  },
  {
    id: 'agents',
    labelKey: 'navigation:menu.agents',
    label: 'Agents',
    icon: Briefcase,
    path: ROUTE_PATHS.AGENTS,
    // Carriers (INSURER / REINSURER / BANCASSURANCE / TPA) appoint agents.
    // BROKER and AGENT tenants don't, so we hide the section for them.
    hideForBusinessTypes: ['BROKER', 'AGENT'],
  },
  // Two entries for the same /relationships page so the label flips with
  // the tenant type. Mutually-exclusive hideForBusinessTypes means exactly
  // one is shown to BROKER/INSURER tenants and neither is shown to others.
  {
    id: 'relationships-broker',
    labelKey: 'navigation:menu.insurers',
    label: 'Insurers',
    icon: Handshake,
    path: ROUTE_PATHS.RELATIONSHIPS,
    hideForBusinessTypes: ['INSURER', 'AGENT', 'BANCASSURANCE', 'REINSURER', 'TPA'],
  },
  {
    id: 'relationships-insurer',
    labelKey: 'navigation:menu.brokers',
    label: 'Brokers',
    icon: Handshake,
    path: ROUTE_PATHS.RELATIONSHIPS,
    hideForBusinessTypes: ['BROKER', 'AGENT', 'BANCASSURANCE', 'REINSURER', 'TPA'],
  },
  {
    id: 'branches',
    labelKey: 'navigation:menu.branches',
    label: 'Branches',
    icon: Building2,
    path: ROUTE_PATHS.BRANCHES,
    hideFromBottomNav: true,
  },
  {
    id: 'payments',
    labelKey: 'navigation:menu.payments',
    label: 'Payments',
    icon: CreditCard,
    path: ROUTE_PATHS.PAYMENTS,
    hideFromBottomNav: true,
  },
  {
    id: 'reconciliation',
    labelKey: 'navigation:menu.reconciliation',
    label: 'Reconciliation',
    icon: FileSpreadsheet,
    path: ROUTE_PATHS.RECONCILIATION,
    permission: 'reconciliation:read',
    hideFromBottomNav: true,
  },
  {
    id: 'products',
    labelKey: 'navigation:menu.products',
    label: 'Products',
    icon: Package,
    path: ROUTE_PATHS.PRODUCTS,
    // Visible to all authenticated users - product-specific permissions can be added later
    hideFromBottomNav: true,
  },
  {
    id: 'users',
    labelKey: 'navigation:menu.users',
    label: 'Users',
    icon: UserCog,
    path: ROUTE_PATHS.USERS,
    permission: 'users:view',
    hideFromBottomNav: true,
  },
  {
    id: 'compliance-documents',
    labelKey: 'navigation:menu.compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    path: ROUTE_PATHS.COMPLIANCE_DOCUMENTS,
    // Only show this nav entry to users who can actually verify
    // documents. Anyone else can still land on the route directly,
    // but the API will return an empty queue for them.
    permission: 'document:verify',
    hideFromBottomNav: true,
  },
];

/**
 * Secondary navigation items
 * These appear at the bottom of sidebar
 */
export const SECONDARY_NAV_ITEMS: NavigationItem[] = [
  {
    id: 'settings',
    labelKey: 'navigation:menu.settings',
    label: 'Settings',
    icon: Settings,
    path: ROUTE_PATHS.SETTINGS,
    hideFromBottomNav: true,
  },
  {
    id: 'help',
    labelKey: 'navigation:menu.help',
    label: 'Help',
    icon: HelpCircle,
    path: ROUTE_PATHS.HELP,
    hideFromBottomNav: true,
  },
];

/**
 * "More" navigation item for mobile bottom nav
 */
export const MORE_NAV_ITEM: NavigationItem = {
  id: 'more',
  labelKey: 'navigation:menu.more',
  label: 'More',
  icon: MoreHorizontal,
  path: '#more',
  mobileOnly: true,
};

/**
 * Bottom nav items (mobile)
 * First 4 main items + "More"
 */
export const BOTTOM_NAV_ITEMS: NavigationItem[] = [
  ...MAIN_NAV_ITEMS.slice(0, 4), // Overview, Policies, Claims, Customers
  MORE_NAV_ITEM,
];

/**
 * All navigation sections for sidebar
 */
export const NAVIGATION_SECTIONS: NavigationSection[] = [
  {
    id: 'main',
    items: MAIN_NAV_ITEMS,
  },
  {
    id: 'secondary',
    items: SECONDARY_NAV_ITEMS,
  },
];

/**
 * Get all navigation items (flat list)
 */
export const getAllNavItems = (): NavigationItem[] => [
  ...MAIN_NAV_ITEMS,
  ...SECONDARY_NAV_ITEMS,
];
